import { Button, TabButtons, Divider, Form } from '@bb/elements-web';
import { useMutation, useQuery } from '@tanstack/react-query';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { RouteComponentProps } from 'react-router-dom';
import { Link } from 'react-router-dom';
import Swal from 'sweetalert2';

import PanelBody from '@app/react-common/PanelBody';
import PanelHeader from '@app/react-common/PanelHeader';
import type { User } from '@bb/users';
import {
  ANNOUNCEMENT_USER_TYPE_MAP,
  type AnnouncementContent,
  type AnnouncementFormData,
  type AnnouncementFormValues,
  type AnnouncementUser,
  getAnnouncement,
  saveAnnouncement,
  saveMessage,
} from 'api/AnnouncementController';
import { Icon } from 'components';
import type { AuthContextUser } from 'contexts/types';
import omit from 'lodash/omit';
import styled from 'styled-components';
import AnnouncementEditForm from './AnnouncementEditForm';
import AnnouncementManageRecipients from './AnnouncementManageRecipients';

export interface Recipients {
  selectedUsers: User[];
  selectedGroups: Array<{
    id: string;
    name: string;
    type?: string;
    userCount?: number;
  }>;
  selectedLocations: Array<{ index: number; name: string }>;
  omittedUsers: User[];
  userTypeFilters: AnnouncementUser[];
}

interface MatchParams {
  announcementId?: string;
}

interface AnnouncementEditViewProps extends RouteComponentProps<MatchParams> {
  auth: AuthContextUser;
}

const AnnouncementEditPage: React.FC<AnnouncementEditViewProps> = ({
  auth,
  match,
  location,
  history,
}) => {
  const isBranchAdmin = auth?.role === 'BRANCH_ADMIN';
  const authorizedLocations: Set<string> | undefined = isBranchAdmin
    ? new Set([auth?.location, ...(auth?.locations || [])])
    : undefined;
  const { email: userEmail } = auth;

  const [form] = Form.useForm<AnnouncementFormValues>();

  const [activeTab, setActiveTab] = useState('create-announcement');
  const [recipients, setRecipients] = useState<Recipients>({
    selectedUsers: [],
    selectedGroups: [],
    selectedLocations: [],
    omittedUsers: [],
    userTypeFilters: [],
  });
  const [uniqueCount, setUniqueCount] = useState(0);

  const searchParams = new URLSearchParams(location.search);
  const draftId = match.params.announcementId;
  const reuseId = searchParams.get('reuse');

  // Set initial form values
  useEffect(() => {
    if (!draftId) {
      form.setFieldsValue({ replyTo: userEmail });
    }
  });

  const onRecipientsChange = useCallback(
    (currRecipients: Recipients | ((prev: Recipients) => Recipients)) => {
      setRecipients(currRecipients);
    },
    [],
  );

  const tabOptions = useMemo(
    () => [
      {
        label: 'Create Announcement',
        value: 'create-announcement',
        icon: <Icon type="antdEditOutlined" height={14} />,
      },
      {
        label: `Manage Recipients (${uniqueCount})`,
        value: 'manage-recipients',
        icon: <Icon type="antdUserGroupAddOutlined" height={14} />,
      },
    ],
    [uniqueCount],
  );

  // Fetch draft announcement
  const { isLoading: isDraftLoading } = useQuery({
    queryKey: ['announcement-draft', draftId],
    queryFn: () => getAnnouncement(draftId),
    enabled: !!draftId,
    onSuccess: data => {
      form.setFieldsValue({
        replyTo: data.replyTo,
        subject: data.subject,
        body: data.body,
        sendLater: false,
      });
    },
    staleTime: Infinity,
    refetchOnMount: 'always',
  });

  // Fetch reuse announcement
  const { isLoading: isReuseLoading } = useQuery({
    queryKey: ['announcement-reuse', reuseId],
    queryFn: () => getAnnouncement(reuseId),
    enabled: !!reuseId,
    onSuccess: data => {
      form.setFieldsValue({
        subject: data.subject,
        body: data.body,
      });
    },
    staleTime: Infinity,
    refetchOnMount: 'always',
  });

  // Send/Schedule announcement mutation
  const sendAnnouncementMutation = useMutation({
    mutationFn: async (values: AnnouncementFormValues) => {
      const { sendLater, delay, ...announcementData } = values;

      const postData = {
        ...announcementData,
        ...(sendLater && delay && { delay }),
        userIds: recipients.selectedUsers.map(user => ({ id: user.id })),
        groups: recipients.selectedGroups,
        locations: recipients.selectedLocations.reduce(
          (indexMap, currLocation) => ({
            ...indexMap,
            [currLocation.index]: true,
          }),
          {},
        ),
        omitIds: recipients.omittedUsers.map(user => user.id),
        omitRoles: recipients.userTypeFilters.map(
          type => ANNOUNCEMENT_USER_TYPE_MAP[type],
        ),
      } as AnnouncementFormData;

      sendLater && delay
        ? await saveAnnouncement(postData)
        : await saveMessage(postData);
    },
    onSuccess: (_, variables) => {
      Swal.fire({
        type: 'success',
        title: variables.sendLater
          ? 'Announcement Scheduled'
          : 'Announcement Sent',
      }).then(navigateToListPage);
    },
    onError: () => {
      Swal.fire({
        type: 'warning',
        title: 'Invalid Announcement',
        text: 'Please double check your announcement to make sure you have added a Subject and some content.',
      });
    },
  });

  // Save draft mutation
  const saveDraftMutation = useMutation({
    mutationFn: async (values: AnnouncementFormValues) => {
      const cleanedDraft: AnnouncementContent = omit(values, [
        'delay',
        'sendLater',
      ]);
      await saveAnnouncement(cleanedDraft);
    },
    onSuccess: () => {
      Swal.fire({
        type: 'success',
        title: 'Draft Announcement Saved',
      }).then(navigateToListPage);
    },
    onError: () => {
      Swal.fire({
        type: 'warning',
        title: 'Saving Failed',
        text: 'Please make sure that the announcement is valid',
      });
    },
  });

  const handleSendAnnouncement = async () => {
    const values = form.getFieldsValue();
    const { sendLater, delay, subject, body } = values;

    // Validations
    if (uniqueCount === 0) {
      return Swal.fire({
        type: 'warning',
        title: 'No Recipients',
        text: 'Please select at least one recipient',
      });
    }

    if (sendLater && (!delay || moment(delay).isBefore(moment()))) {
      return Swal.fire({
        type: 'warning',
        title: 'Time Provided Is Not Valid',
        text: 'Please specify valid time in the future',
      });
    }

    if (!subject || !body || subject.trim().length === 0) {
      return Swal.fire({
        type: 'warning',
        title: 'Invalid Announcement',
        text: 'Please double check your announcement to make sure you have added a Subject and some content.',
      });
    }

    const { value } = await Swal.fire({
      type: 'info',
      title: 'Are you sure?',
      text: 'Click "Yes" to send the announcement!',
      showCancelButton: true,
      confirmButtonText: 'Yes',
    });

    if (value) {
      Swal.fire({
        type: 'info',
        title: sendLater
          ? 'Scheduling Announcement...'
          : 'Sending Announcement...',
        showConfirmButton: false,
        html: '<i class="fa fa-spinner fa-spin fa-2x"></i>',
      });
      sendAnnouncementMutation.mutate(values);
    }
  };

  const handleSaveDraft = async () => {
    const values = form.getFieldsValue();
    const { subject, body } = values;

    if (!subject || !body || subject.trim().length === 0) {
      return Swal.fire({
        type: 'warning',
        title: 'Invalid Announcement',
        text: 'Please double check your announcement to make sure you have added a Subject and some content.',
      });
    }

    Swal.fire({
      type: 'info',
      title: 'Saving Draft Announcement...',
      showConfirmButton: false,
      html: '<i class="fa fa-spinner fa-spin fa-2x"></i>',
    });
    saveDraftMutation.mutate(values);
  };

  const navigateToListPage = () => {
    history.replace('/announcement/list');
  };

  const sendLater = Form.useWatch('sendLater', form) ?? false;

  const isLoading =
    (!!draftId && isDraftLoading) || (!!reuseId && isReuseLoading);

  const sendAnnouncementButton = (
    <Button
      type="primary"
      color="green"
      dataCy="send-schedule-announcement-button"
      onClick={handleSendAnnouncement}
      key="send-schedule-announcement-button"
      size="small"
    >
      {sendLater ? 'Schedule' : 'Send'} Announcement
    </Button>
  );

  const saveAnnouncementButton = (
    <Button
      dataCy="save-draft-announcement-button"
      onClick={handleSaveDraft}
      key="save-draft-announcement-button"
      size="small"
      minWidth={70}
    >
      Save Draft
    </Button>
  );

  const cancelButton = (
    <Link key="cancel-edit-button-link" to="/announcement/list">
      <Button
        type="secondary"
        size="small"
        dataCy="cancel-edit-button"
        minWidth={70}
      >
        Cancel
      </Button>
    </Link>
  );

  const extras = [sendAnnouncementButton, saveAnnouncementButton, cancelButton];

  return (
    <StyledRootContainer className="global--angular">
      <PanelHeader title="New Announcement" extra={extras} />
      <PanelBody style={{ flexDirection: 'column' }}>
        <TabButtons
          dataCy="announcement-edit-page-tabs"
          options={tabOptions}
          value={activeTab}
          onChange={e => setActiveTab(e.target.value)}
        />
        <Divider />
        {!isLoading && (
          // Do not unmount component to preserve form state
          <StyledAnnouncementEditContainer $activeTab={activeTab}>
            <AnnouncementEditForm form={form as any} />
          </StyledAnnouncementEditContainer>
        )}

        {activeTab === 'manage-recipients' && (
          <AnnouncementManageRecipients
            recipients={recipients}
            authorizedLocations={authorizedLocations}
            onUniqueCountChange={setUniqueCount}
            onRecipientsChange={onRecipientsChange}
          />
        )}
      </PanelBody>
    </StyledRootContainer>
  );
};

export default AnnouncementEditPage;

const StyledRootContainer = styled.div`
  > .content {
    padding-bottom: 0;
  }
`;

const StyledAnnouncementEditContainer = styled.div<{ $activeTab: string }>`
  display: ${props =>
    props.$activeTab === 'create-announcement' ? 'block' : 'none'};
  padding: 0 65px;

  @media screen and (max-width: 992px) {
    padding: 0 12px;
  }
`;
